import React, { useState, useEffect, useReducer } from 'react';
import { useParams } from 'react-router-dom';
import CuadroListaArbitro from './CuadroListaArbitro.js';
import CuadroEditableJugador from './CuadroEditableJugador.js';
import CuadroEditablePersona from './CuadroEditablePersona.js';
import CuadroEditableTecnico from './CuadroEditableTecnico.js';
import EstadisticasPersona from './EstadisticasPersona.js';
import LinksPersona from './LinksPersona.js';
import ParientesPersona from './ParientesPersona.js';
import TorneosPersona from './TorneosPersona.js';
import TrayectoriaClubes from './TrayectoriaClubes.js';
import TrayectoriaSeleccion from './TrayectoriaSeleccion.js';
import FichaSolapas from '../UI/FichaSolapas.js';
import { FichaColumnas, FichaColumna } from '../UI/FichaColumnas.js';
import FichaGrilla from '../UI/FichaGrilla.js';
import { Cuadro, NombreApellido } from '../funciones/FuncionesPersona.js';
import * as Data from '../data/DataPersonas.js';
import * as Reducer from '../reducers/ReducerDataAccess.js';
import InformacionesPersona from './InformacionesPersona.js';
import BoletinesPersona from './BoletinesPersona.js';

const Solapas = {
  DATOS: 'Datos',
  JUGADOR: 'Como Jugador',
  TECNICO: 'Como Técnico',
  ARBITRO: 'Como Arbitro',
  TORNEOS: 'Torneos',
  ESTADISTICAS: 'Estadisticas',
};

const DatosVacio = {
  persona: null,
  jugador: null,
  tecnico: null,
  arbitro: null,
};

const DatosPersona = () => {
  const { id: idPersona } = useParams();
  const [datos, setDatos] = useState(DatosVacio);
  const [solapas, setSolapas] = useState([]);
  const [dataAccess, setDataAccess] = useReducer(Reducer.reducerDataAccess, Reducer.ReducerVacio);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const esJugador = datos.persona && datos.persona.esJugador;
  const esTecnico = datos.persona && datos.persona.esTecnico;
  const esArbitro = datos.persona && datos.persona.esArbitro;

  useEffect(() => {
    if (datos.persona) {
      const listaSolapas = [
        { nombre: Solapas.DATOS, visible: true },
        { nombre: Solapas.TORNEOS, visible: true },
        { nombre: Solapas.JUGADOR, visible: datos.persona.esJugador },
        { nombre: Solapas.TECNICO, visible: datos.persona.esTecnico },
        { nombre: Solapas.ARBITRO, visible: datos.persona.esArbitro },
        { nombre: Solapas.ESTADISTICAS, visible: true },
      ];
      setSolapas(listaSolapas);
    }
  }, [datos.persona]);

  // Detectar cambios de tamaño de pantalla
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const cargarPersona = async () => {
      setDataAccess({ type: Reducer.Accion.CARGANDO, value: true });
      try {
        const datosPersona = await Data.GetPersona(idPersona);
        if (datosPersona && datosPersona !== Promise.resolve(datosPersona)) {
          setDatos(datosPersona);
        }
        setDataAccess({ type: Reducer.Accion.LIMPIAR, value: null });
      } catch (error) {
        setDataAccess({ type: Reducer.Accion.ERRORES, value: [error.message] });
      }
    };

    if (idPersona === null) {
      setDatos(DatosVacio);
      setDataAccess({ type: Reducer.Accion.LIMPIAR, value: null });
    } else {
      cargarPersona();
    }
  }, [idPersona]);

  const mostrarDatosSolapa = (solapa) => {
    switch (solapa) {
      case Solapas.DATOS: {
        // Orden para desktop (original): izquierda/derecha con columnaSeparada=7
        const itemsIzq = [
          {
            id: 'nombres',
            componente: <CuadroEditablePersona cuadro={Cuadro.NOMBRES} persona={datos.persona} />,
          },
          {
            id: 'generales',
            componente: <CuadroEditablePersona cuadro={Cuadro.GENERALES} persona={datos.persona} />,
          },
          {
            id: 'nacimiento',
            componente: (
              <CuadroEditablePersona cuadro={Cuadro.NACIMIENTO} persona={datos.persona} />
            ),
          },
          {
            id: 'fallecimiento',
            componente: (
              <CuadroEditablePersona cuadro={Cuadro.FALLECIMIENTO} persona={datos.persona} />
            ),
          },
          { id: 'parientes', componente: <ParientesPersona persona={datos.persona} /> },
          { id: 'links', componente: <LinksPersona persona={datos.persona} /> },
          { id: 'informaciones', componente: <InformacionesPersona persona={datos.persona} /> },
        ];

        const itemsDer =
          esJugador || esTecnico
            ? [
                {
                  id: 'trayectoria_clubes',
                  componente: <TrayectoriaClubes persona={datos.persona} />,
                },
                {
                  id: 'trayectoria_seleccion',
                  componente: <TrayectoriaSeleccion persona={datos.persona} />,
                },
              ]
            : [];

        // En mobile: reordenar items para mostrar Trayectoria antes de Parientes/Links/Informaciones
        if (isMobile) {
          const itemsMobile = [
            itemsIzq[0], // Nombres
            itemsIzq[1], // Generales
            itemsIzq[2], // Nacimiento
            itemsIzq[3], // Fallecimiento
            ...itemsDer, // Trayectoria Clubes, Trayectoria Selecciones
            itemsIzq[4], // Parientes
            itemsIzq[5], // Links
            itemsIzq[6], // Informaciones
          ];
          return <FichaGrilla items={itemsMobile} />;
        }

        // En desktop: mantener orden original con dos columnas
        const itemsCompletos = [...itemsIzq, ...itemsDer];
        return <FichaGrilla items={itemsCompletos} columnaSeparada={7} />;
      }
      case Solapas.TORNEOS: {
        return (
          <FichaColumnas>
            <FichaColumna width="80%">
              {(esJugador || esTecnico) && <TorneosPersona persona={datos.persona} />}
            </FichaColumna>
          </FichaColumnas>
        );
      }
      case Solapas.JUGADOR: {
        return (
          <FichaColumnas>
            <FichaColumna width="80%">
              {esJugador && datos.jugador && (
                <React.Fragment>
                  <CuadroEditableJugador cuadro={Cuadro.JUGADOR} jugador={datos.jugador} />
                  <BoletinesPersona persona={datos.persona} />
                </React.Fragment>
              )}
            </FichaColumna>
          </FichaColumnas>
        );
      }
      case Solapas.TECNICO: {
        return (
          <FichaColumnas>
            <FichaColumna width="80%">
              {esTecnico && datos.tecnico && (
                <CuadroEditableTecnico cuadro={Cuadro.TECNICO} tecnico={datos.tecnico} />
              )}
            </FichaColumna>
          </FichaColumnas>
        );
      }
      case Solapas.ARBITRO: {
        return (
          <FichaColumnas>
            <FichaColumna width="50rem">
              {esArbitro && datos.persona && (
                <CuadroListaArbitro cuadro={Cuadro.ARBITRO} persona={datos.persona} />
              )}
            </FichaColumna>
          </FichaColumnas>
        );
      }
      case Solapas.ESTADISTICAS: {
        return (
          <FichaColumnas>
            <FichaColumna width="80%">
              <EstadisticasPersona persona={datos.persona} />
            </FichaColumna>
          </FichaColumnas>
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <FichaSolapas
      item={datos.persona}
      titulo={NombreApellido(datos.persona)}
      solapas={solapas}
      solapaInicial={Solapas.DATOS}
      isCargando={dataAccess.cargando}
      errores={dataAccess.errores}
      mostrarDatosSolapa={mostrarDatosSolapa}
    />
  );
};

export default DatosPersona;
